import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, Scale } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import ClaimParametersSection from './ClaimParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import LawyerFeesSection from './LawyerFeesSection';
import AdditionalExpensesSection from './AdditionalExpensesSection';
import BenefitsSection from './BenefitsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент основной формы расчета стоимости арбитража
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  feeSchedule,
  setFeeSchedule,
  courtTypes,
  claimTypes,
  disputeCategories,
  benefitTypes,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Сброс связанных полей, если функция отключена
      if (name === 'includeLawyerFees' && !checked) {
        newState.lawyerHourlyRate = '5000';
        newState.estimatedHours = '10';
      }
      
      if (name === 'includeExpertFees' && !checked) {
        newState.expertFeeAmount = '30000';
      }
      
      if (name === 'includeOtherExpenses' && !checked) {
        newState.otherExpensesAmount = '10000';
      }
      
      if (name === 'applyBenefits' && !checked) {
        newState.benefitType = 'none';
      }
      
      if (name === 'installmentPlan' && !checked) {
        newState.installmentMonths = '3';
      }

      // Для некоторых полей может потребоваться дополнительная логика изменения зависимых полей
      if (name === 'courtType') {
        // Например, изменить тип иска в зависимости от типа суда
        if (value === 'arbitration') {
          if (['non_property'].includes(prev.claimType)) {
            newState.claimType = 'property';
          }
        }
        
        if (value === 'civil') {
          if (['bankruptcy'].includes(prev.claimType)) {
            newState.claimType = 'property';
          }
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      claimAmount, courtType, claimType, disputeCategory, plaintiffStatus, defendantCount,
      includeLawyerFees, lawyerHourlyRate, estimatedHours,
      includeExpertFees, expertFeeAmount,
      includeOtherExpenses, otherExpensesAmount,
      applyBenefits, benefitType,
      installmentPlan, installmentMonths
    } = formData;

    // Парсинг числовых значений
    const numClaimAmount = parseFloatOrDefault(claimAmount, -1);
    const numDefendantCount = parseIntOrDefault(defendantCount, -1);
    const numLawyerHourlyRate = parseFloatOrDefault(lawyerHourlyRate, -1);
    const numEstimatedHours = parseFloatOrDefault(estimatedHours, -1);
    const numExpertFeeAmount = parseFloatOrDefault(expertFeeAmount, -1);
    const numOtherExpensesAmount = parseFloatOrDefault(otherExpensesAmount, -1);
    const numInstallmentMonths = parseIntOrDefault(installmentMonths, -1);

    // Валидация базовых полей
    if (numClaimAmount <= 0) {
      newErrors.claimAmount = 'Сумма иска должна быть положительной.';
    } else if (numClaimAmount > 1000000000) {
      newErrors.claimAmount = 'Максимальная сумма иска для расчета 1 млрд ₽.';
    }
    
    if (!courtType || !courtTypes.some(c => c.id === courtType)) {
      newErrors.courtType = 'Выберите тип суда.';
    }
    
    if (!claimType || !claimTypes.some(c => c.id === claimType)) {
      newErrors.claimType = 'Выберите тип иска.';
    }
    
    if (!disputeCategory || !disputeCategories.some(c => c.id === disputeCategory)) {
      newErrors.disputeCategory = 'Выберите категорию спора.';
    }
    
    if (!plaintiffStatus || !['company', 'individual', 'entrepreneur'].includes(plaintiffStatus)) {
      newErrors.plaintiffStatus = 'Выберите статус заявителя.';
    }
    
    if (!isPositiveNumber(numDefendantCount) || !isInteger(defendantCount)) {
      newErrors.defendantCount = 'Укажите корректное количество ответчиков.';
    } else if (numDefendantCount > 100) {
      newErrors.defendantCount = 'Слишком большое количество ответчиков.';
    }

    // Валидация расходов на юриста
    if (includeLawyerFees) {
      if (numLawyerHourlyRate <= 0) {
        newErrors.lawyerHourlyRate = 'Ставка должна быть положительной.';
      } else if (numLawyerHourlyRate > 50000) {
        newErrors.lawyerHourlyRate = 'Ставка не может превышать 50,000 ₽/час.';
      }
      
      if (numEstimatedHours <= 0) {
        newErrors.estimatedHours = 'Количество часов должно быть положительным.';
      } else if (numEstimatedHours > 1000) {
        newErrors.estimatedHours = 'Слишком большое количество часов.';
      }
    }

    // Валидация расходов на экспертизу
    if (includeExpertFees && numExpertFeeAmount <= 0) {
      newErrors.expertFeeAmount = 'Сумма должна быть положительной.';
    }

    // Валидация прочих расходов
    if (includeOtherExpenses && numOtherExpensesAmount <= 0) {
      newErrors.otherExpensesAmount = 'Сумма должна быть положительной.';
    }

    // Валидация льгот
    if (applyBenefits && (!benefitType || benefitType === 'none')) {
      newErrors.benefitType = 'Выберите тип льготы.';
    }

    // Валидация рассрочки
    if (installmentPlan) {
      if (!isPositiveNumber(numInstallmentMonths) || !isInteger(installmentMonths)) {
        newErrors.installmentMonths = 'Укажите корректное количество месяцев.';
      } else if (numInstallmentMonths > 36) {
        newErrors.installmentMonths = 'Максимальный срок рассрочки 36 месяцев.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, courtTypes, claimTypes, disputeCategories, setErrors]);

  // Расчет госпошлины для арбитражного суда (имущественные требования)
  const calculateArbitrationCourtFee = (amount) => {
    if (amount <= 100000) {
      return amount * 0.04;
    } else if (amount <= 200000) {
      return 4000 + (amount - 100000) * 0.03;
    } else if (amount <= 1000000) {
      return 7000 + (amount - 200000) * 0.02;
    } else if (amount <= 2000000) {
      return 23000 + (amount - 1000000) * 0.01;
    } else {
      return 33000 + (amount - 2000000) * 0.005;
    }
  };

  // Расчет госпошлины для суда общей юрисдикции (имущественные требования)
  const calculateCivilCourtFee = (amount) => {
    if (amount <= 20000) {
      return amount * 0.04;
    } else if (amount <= 100000) {
      return 800 + (amount - 20000) * 0.03;
    } else if (amount <= 200000) {
      return 3200 + (amount - 100000) * 0.02;
    } else if (amount <= 1000000) {
      return 5200 + (amount - 200000) * 0.01;
    } else {
      return 13200 + (amount - 1000000) * 0.005;
    }
  };

  // Расчет госпошлины для неимущественных требований
  const calculateNonPropertyFee = (courtType) => {
    switch (courtType) {
      case 'arbitration': return 6000;
      case 'civil': return 300;
      case 'administrative': return 3000;
      case 'commercial': return 15000;
      default: return 6000;
    }
  };

  // Расчет госпошлины с учетом льгот
  const applyFeeBenefits = (fee, benefitType, plaintiffStatus) => {
    let discountRate = 1.0; // Без скидки
    
    if (plaintiffStatus === 'individual') {
      switch (benefitType) {
        case 'social': discountRate = 0.5; break;
        case 'disabled': discountRate = 0; break;
        case 'pensioner': discountRate = 0.5; break;
        case 'veteran': discountRate = 0; break;
        default: discountRate = 1.0;
      }
    }
    
    return fee * discountRate;
  };

  // Формирование шкалы госпошлин для наглядности
  const generateFeeSchedule = (courtType, claimType) => {
    const schedule = [];
    
    if (claimType === 'property') {
      if (courtType === 'arbitration') {
        schedule.push({ min: 0, max: 100000, fee: "4% от суммы иска" });
        schedule.push({ min: 100000, max: 200000, fee: "4000 ₽ + 3% от суммы, превышающей 100 000 ₽" });
        schedule.push({ min: 200000, max: 1000000, fee: "7000 ₽ + 2% от суммы, превышающей 200 000 ₽" });
        schedule.push({ min: 1000000, max: 2000000, fee: "23000 ₽ + 1% от суммы, превышающей 1 000 000 ₽" });
        schedule.push({ min: 2000000, max: Infinity, fee: "33000 ₽ + 0,5% от суммы, превышающей 2 000 000 ₽" });
      } else if (courtType === 'civil') {
        schedule.push({ min: 0, max: 20000, fee: "4% от суммы иска, но не менее 400 ₽" });
        schedule.push({ min: 20000, max: 100000, fee: "800 ₽ + 3% от суммы, превышающей 20 000 ₽" });
        schedule.push({ min: 100000, max: 200000, fee: "3200 ₽ + 2% от суммы, превышающей 100 000 ₽" });
        schedule.push({ min: 200000, max: 1000000, fee: "5200 ₽ + 1% от суммы, превышающей 200 000 ₽" });
        schedule.push({ min: 1000000, max: Infinity, fee: "13200 ₽ + 0,5% от суммы, превышающей 1 000 000 ₽" });
      } else if (courtType === 'administrative') {
        schedule.push({ min: 0, max: Infinity, fee: "Фиксированный размер 2000 ₽" });
      } else if (courtType === 'commercial') {
        schedule.push({ min: 0, max: 1000000, fee: "1,5% от суммы иска, но не менее 5000 ₽" });
        schedule.push({ min: 1000000, max: 10000000, fee: "15000 ₽ + 0,8% от суммы, превышающей 1 000 000 ₽" });
        schedule.push({ min: 10000000, max: Infinity, fee: "87000 ₽ + 0,3% от суммы, превышающей 10 000 000 ₽" });
      }
    } else {
      // Неимущественные требования
      if (courtType === 'arbitration') {
        schedule.push({ min: 0, max: 0, fee: "Фиксированный размер 6000 ₽" });
      } else if (courtType === 'civil') {
        schedule.push({ min: 0, max: 0, fee: "Фиксированный размер 300 ₽" });
      } else if (courtType === 'administrative') {
        schedule.push({ min: 0, max: 0, fee: "Фиксированный размер 3000 ₽" });
      } else if (courtType === 'commercial') {
        schedule.push({ min: 0, max: 0, fee: "Фиксированный размер 15000 ₽" });
      }
    }
    
    return schedule;
  };

  // Функция расчета стоимости арбитража
  const calculateArbitrationCost = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setFeeSchedule([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const numClaimAmount = parseFloatOrDefault(formData.claimAmount);
        const courtType = formData.courtType;
        const claimType = formData.claimType;
        const disputeCategory = formData.disputeCategory;
        const plaintiffStatus = formData.plaintiffStatus;
        const numDefendantCount = parseIntOrDefault(formData.defendantCount);
        
        const includeLawyerFees = formData.includeLawyerFees;
        const numLawyerHourlyRate = parseFloatOrDefault(formData.lawyerHourlyRate);
        const numEstimatedHours = parseFloatOrDefault(formData.estimatedHours);
        
        const includeExpertFees = formData.includeExpertFees;
        const numExpertFeeAmount = parseFloatOrDefault(formData.expertFeeAmount);
        
        const includeOtherExpenses = formData.includeOtherExpenses;
        const numOtherExpensesAmount = parseFloatOrDefault(formData.otherExpensesAmount);
        
        const applyBenefits = formData.applyBenefits;
        const benefitType = formData.benefitType;
        
        const installmentPlan = formData.installmentPlan;
        const numInstallmentMonths = parseIntOrDefault(formData.installmentMonths);

        // --- Расчет государственной пошлины ---
        let courtFee = 0;
        
        if (claimType === 'property') {
          // Имущественные требования
          if (courtType === 'arbitration') {
            courtFee = calculateArbitrationCourtFee(numClaimAmount);
          } else if (courtType === 'civil') {
            courtFee = calculateCivilCourtFee(numClaimAmount);
          } else if (courtType === 'administrative') {
            courtFee = 2000; // Фиксированная пошлина
          } else if (courtType === 'commercial') {
            // Коммерческий арбитраж
            courtFee = Math.max(5000, numClaimAmount * 0.015);
            if (numClaimAmount > 1000000) {
              courtFee = 15000 + (numClaimAmount - 1000000) * 0.008;
            }
            if (numClaimAmount > 10000000) {
              courtFee = 87000 + (numClaimAmount - 10000000) * 0.003;
            }
          }
        } else {
          // Неимущественные требования
          courtFee = calculateNonPropertyFee(courtType);
          
          // Для определенных типов исков могут быть особые ставки
          if (claimType === 'bankruptcy' && courtType === 'arbitration') {
            courtFee = 6000;
          }
          
          if (claimType === 'administrative' && courtType === 'administrative') {
            courtFee = 2000;
          }
        }
        
        // Применение льгот и скидок
        let discountedCourtFee = courtFee;
        if (applyBenefits) {
          discountedCourtFee = applyFeeBenefits(courtFee, benefitType, plaintiffStatus);
        }
        
        // Расчет размера госпошлины с учетом рассрочки (первоначальный платеж)
        let initialPayment = discountedCourtFee;
        let monthlyPayment = 0;
        
        if (installmentPlan && numInstallmentMonths > 0) {
          initialPayment = discountedCourtFee / 2; // 50% сразу
          monthlyPayment = (discountedCourtFee / 2) / numInstallmentMonths; // Остальное равными частями
        }
        
        // --- Расчет дополнительных расходов ---
        // Расходы на юриста
        let lawyerFees = 0;
        if (includeLawyerFees) {
          lawyerFees = numLawyerHourlyRate * numEstimatedHours;
        }
        
        // Расходы на экспертизу
        let expertFees = 0;
        if (includeExpertFees) {
          expertFees = numExpertFeeAmount;
        }
        
        // Прочие расходы
        let otherExpenses = 0;
        if (includeOtherExpenses) {
          otherExpenses = numOtherExpensesAmount;
        }
        
        // Дополнительные сборы в зависимости от типа суда и спора
        let additionalFees = 0;
        
        // Для арбитражного суда в упрощенном производстве уменьшенный размер госпошлины
        if (courtType === 'arbitration' && disputeCategory === 'simplified') {
          additionalFees -= discountedCourtFee * 0.3; // Скидка 30% на госпошлину
        }
        
        // Для большого количества ответчиков дополнительная госпошлина
        if (numDefendantCount > 1) {
          additionalFees += (discountedCourtFee * 0.1) * (numDefendantCount - 1);
        }
        
        // --- Итоговые суммы ---
        const totalCourtFee = discountedCourtFee;
        const totalLegalFees = lawyerFees + expertFees + otherExpenses + additionalFees;
        const totalCost = totalCourtFee + totalLegalFees;
        
        // --- Шкала госпошлин ---
        const feeScheduleData = generateFeeSchedule(courtType, claimType);
        
        // --- Формирование результатов ---
        const finalResults = {
          claimAmount: numClaimAmount,
          courtType: courtType,
          claimType: claimType,
          disputeCategory: disputeCategory,
          
          // Госпошлины
          originalCourtFee: courtFee,
          discountedCourtFee: discountedCourtFee,
          initialPayment: initialPayment,
          monthlyPayment: monthlyPayment,
          installmentMonths: installmentPlan ? numInstallmentMonths : 0,
          
          // Дополнительные расходы
          lawyerFees: lawyerFees,
          expertFees: expertFees,
          otherExpenses: otherExpenses,
          additionalFees: additionalFees,
          
          // Итоговые суммы
          totalCourtFee: totalCourtFee,
          totalLegalFees: totalLegalFees,
          totalCost: totalCost,
          
          // Расчет по суду и истцу
          plaintiffStatus: plaintiffStatus,
          defendantCount: numDefendantCount,
          
          // Льготы
          discountRate: applyBenefits ? (discountedCourtFee / courtFee) : 1.0,
          benefitType: benefitType
        };
        
        setResults(finalResults);
        setFeeSchedule(feeScheduleData);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setFeeSchedule([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setFeeSchedule, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      claimAmount: '100000',
      courtType: 'arbitration', 
      claimType: 'property', 
      disputeCategory: 'general', 
      plaintiffStatus: 'company',
      defendantCount: '1',
      includeLawyerFees: false,
      lawyerHourlyRate: '5000',
      estimatedHours: '10',
      includeExpertFees: false,
      expertFeeAmount: '30000',
      includeOtherExpenses: false,
      otherExpensesAmount: '10000',
      applyBenefits: false,
      benefitType: 'none',
      installmentPlan: false,
      installmentMonths: '3'
    });
    setResults(null);
    setFeeSchedule([]);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }
    
    // Заголовок CSV
    const headers = ['Параметр', 'Значение'];
    
    // Данные для экспорта
    const rows = [
      ['Сумма иска', formatCurrency(results.claimAmount, 2)],
      ['Тип суда', courtTypes.find(c => c.id === results.courtType)?.name || results.courtType],
      ['Тип иска', claimTypes.find(c => c.id === results.claimType)?.name || results.claimType],
      ['Категория спора', disputeCategories.find(c => c.id === results.disputeCategory)?.name || results.disputeCategory],
      [''],
      ['Размер госпошлины (без льгот)', formatCurrency(results.originalCourtFee, 2)],
      ['Размер госпошлины (с учетом льгот)', formatCurrency(results.discountedCourtFee, 2)],
      [''],
      ['Расходы на юриста', formatCurrency(results.lawyerFees, 2)],
      ['Расходы на экспертизу', formatCurrency(results.expertFees, 2)],
      ['Иные расходы', formatCurrency(results.otherExpenses, 2)],
      ['Дополнительные сборы', formatCurrency(results.additionalFees, 2)],
      [''],
      ['ИТОГО госпошлина', formatCurrency(results.totalCourtFee, 2)],
      ['ИТОГО дополнительные расходы', formatCurrency(results.totalLegalFees, 2)],
      ['ВСЕГО расходы по делу', formatCurrency(results.totalCost, 2)]
    ];
    
    // Если есть рассрочка, добавляем информацию о ней
    if (results.installmentMonths > 0) {
      rows.splice(7, 0, 
        ['Первоначальный взнос', formatCurrency(results.initialPayment, 2)],
        ['Ежемесячный платеж', formatCurrency(results.monthlyPayment, 2)],
        ['Срок рассрочки', `${results.installmentMonths} мес.`]
      );
    }
    
    // Формирование содержимого CSV
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');
    
    // Создание и скачивание файла
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_судебных_расходов.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateArbitrationCost();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Scale className="w-6 h-6 text-indigo-500" />
          Параметры расчета
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры иска */}
        <ClaimParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          courtTypes={courtTypes}
          claimTypes={claimTypes}
          disputeCategories={disputeCategories}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Услуги юриста */}
        <LawyerFeesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Дополнительные расходы */}
        <AdditionalExpensesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Льготы и рассрочка */}
        <BenefitsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          benefitTypes={benefitTypes}
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать расходы'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать расчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;